/**
 * Metadati del registro attività multi-flusso: ordine/etichette/icone delle
 * sotto-voci di menu e la mappa subject_type → link profondo.
 *
 * ATTENZIONE — mapping non verificato contro il backend reale.
 *   Il contratto descritto per questo pacchetto (query param `flow` e
 *   `tenant_id=*` su GET /api/v1/audit, colonne `subject_type`/`subject_id`,
 *   costanti `SUBJECT_*` in src/api/routes/audit.py) NON esiste in
 *   origin/main né in nessun altro branch del repo al momento in cui questo
 *   pacchetto è stato scritto (verificato leggendo il file ed eseguendo un
 *   grep su tutti i branch remoti). Le pratiche DDT e le fatture Rinascente
 *   oggi vengono scritte in audit_log con `request_id = NULL` e l'id vero
 *   solo dentro il JSON opaco `details` (`details.ddt_practice_id`,
 *   `details.rinascente_invoice_id`) — quindi introdurre `subject_type`/
 *   `subject_id` richiede anche una migration e modifiche a più call-site
 *   Python, non solo l'aggiunta di due colonne SELECT.
 *
 *   I valori di SUBJECT_TYPE_TO_PATH sotto sono stati scritti come ipotesi
 *   ricavata dai nomi delle tabelle reali, e sono poi risultati **corretti**
 *   uno per uno contro le costanti `SUBJECT_*` di `src/api/routes/audit.py`.
 *   La coincidenza non è però una garanzia, quindi ora è un vincolo
 *   verificato: `tests/test_frontend_audit_subject_map.py` fallisce se questa
 *   mappa e quelle costanti divergono, da qualunque dei due lati venga la
 *   modifica. Senza quel test un soggetto rinominato renderebbe la riga
 *   semplicemente non cliccabile, cioè un difetto invisibile.
 *
 *   `delegation_check` è l'unica voce senza corrispettivo nel backend, e non
 *   è un errore: le deleghe non scrivono NESSUNA riga di audit. La voce resta
 *   in attesa di quel flusso, dichiarata come tale nel test.
 */
import type { AuditFlow } from "../api/types";

export const AUDIT_FLOW_ORDER: AuditFlow[] = [
  "rdi",
  "ddt",
  "bsc",
  "deleghe",
  "rinascente",
  "conversazioni",
  "piattaforma",
];

export const AUDIT_FLOW_META: Record<AuditFlow, { label: string; icon: string }> = {
  rdi: { label: "RDI", icon: "assignment" },
  ddt: { label: "DDT", icon: "local_shipping" },
  bsc: { label: "BSC", icon: "description" },
  deleghe: { label: "Deleghe", icon: "verified_user" },
  rinascente: { label: "Rinascente", icon: "receipt_long" },
  // "Conversazioni", non "Agente": quest'ultima è l'etichetta della voce di
  // navigazione verso /conversazioni (Sidebar.tsx, AGENTE_ITEM) — usarla
  // anche qui renderebbe ambiguo ogni testo/test che cerca "Agente" nel
  // menu, dato che le due voci convivono nello stesso tenant.
  conversazioni: { label: "Conversazioni", icon: "forum" },
  // Eventi trasversali (magic link, credenziali di canale, ricalcolo costi):
  // non un modulo di tenant, quindi non gated da `modules` — solo dal ruolo
  // platform_admin (vedi visibilità in Sidebar.tsx).
  piattaforma: { label: "Piattaforma", icon: "hub" },
};

/** Mappa subject_type → costruttore del link profondo. Vedi l'avviso in
 *  testa al file: valori non verificati tranne `conversation_session`. */
const SUBJECT_TYPE_TO_PATH: Record<string, (id: string) => string> = {
  conversation_session: (id) => `/conversazioni/${id}`,
  processing_request: (id) => `/review3/${id}`,
  ddt_practice: (id) => `/ddt/${id}`,
  bsc_practice: (id) => `/bsc/${id}`,
  rinascente_invoice: (id) => `/rinascente/${id}`,
  delegation_check: (id) => `/deleghe/${id}`,
};

/** Link profondo per una riga di audit, o `null` se la riga non ha un
 *  soggetto (subject_type/subject_id assenti) o il subject_type non è nella
 *  mappa — in entrambi i casi la riga non deve apparire cliccabile. */
export function subjectDeepLink(subjectType?: string | null, subjectId?: string | null): string | null {
  if (!subjectType || !subjectId) return null;
  const build = SUBJECT_TYPE_TO_PATH[subjectType];
  return build ? build(subjectId) : null;
}
